/**
 * TASK-836 — keep the canvas and its address in step.
 *
 * Follows the Radar's conventions exactly (`ChangeRadar.tsx:40-53`, TASK-726), because they
 * are already reviewed and because two competing URL idioms on one surface is worse than
 * either:
 *
 *   * `{ replace: true }` — working on the canvas must not fill the history stack, or Back
 *     becomes a walk through every tile the advisor opened;
 *   * **the default is DELETED, never written** — an empty canvas keeps a clean URL, so "is
 *     this the default state?" stays answerable from the address.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCanvasStore } from '../stores/canvasStore';
import { registry } from '../registry/registry';
import { CANVAS_PARAM, decodeCanvas, encodeCanvas } from '../stores/canvasUrl';

/**
 * What the restore could not do, for the ADVISOR rather than the console (TASK-842).
 *
 * `null` means the link restored completely — silence has to actually mean complete, or the
 * notice stops being information.
 */
export interface CanvasRestoreNotice {
  /** Components the link named that this build no longer has. May be empty. */
  unknown: string[];
  /** Part or all of the param could not be read at all. */
  malformed: boolean;
  /**
   * Tiles the link asked for and did not get, in TOTAL — named ones plus unreadable entries.
   * Not `unknown.length`: a link losing one named widget and one unreadable entry would then
   * report "One tile", with the named one shown beside it implying that was the whole story.
   */
  missing: number;
  /**
   * Did anything restore at all? A wholly unreadable param restores nothing, and the notice
   * must not then claim "Everything else was restored" over an empty canvas — that is the
   * same shape of defect as the badge and the prose disagreeing on a re-profile tile.
   */
  restoredAny: boolean;
}

/**
 * The canvas as one comparable string. Order included — a reordered canvas is a changed one.
 *
 * Ids alone are not enough, and the reason is specific: `addSpecs` UPDATES a same-identity
 * tile in place rather than appending (`canvasStore.ts:288`), so re-summoning a view with new
 * filters changes what is on screen and not the id list. `collapsed` and `rail` are the same
 * case — pinning a tile to the rail takes it out of the grid entirely. `updatedAt` is what the
 * store moves on an in-place update, so it is the field that makes this honest.
 */
const tileFingerprint = (
  specs: { id: string; collapsed?: boolean; rail?: string; updatedAt?: number }[],
) => specs.map((s) => `${s.id}:${s.updatedAt ?? 0}:${s.collapsed ? 1 : 0}:${s.rail ?? ''}`)
  .join('|');

export function useCanvasUrlSync(enabled = true) {
  const [searchParams, setSearchParams] = useSearchParams();
  const specs = useCanvasStore((s) => s.specs);
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  // Restore runs ONCE. Re-running it would re-add tiles the advisor had just closed — the
  // store is the live truth after boot, and the URL is its projection.
  const restored = useRef(false);
  //: WHICH tiles the canvas held once the restore finished. `null` until then. Identities
  //: rather than a count, because `addSpecs` updates a same-identity tile IN PLACE — so a
  //: count cannot tell "nothing happened" from "one tile closed and another opened".
  const restoredTiles = useRef<string | null>(null);
  // TASK-842: TASK-836 AC5 asked that an incomplete restore "say so". Two thirds of it held —
  // the SPA stayed up and nothing rendered blind — but the saying-so was a `console.warn`,
  // and the comment beneath it was right about why it mattered ("a fact the person who
  // followed it should hear") while telling the console instead of the person. The advisor
  // who followed a colleague's link saw a canvas with one tile missing and no indication that
  // anything was absent. That is the case that actually occurs: a link shared across a deploy
  // that removed or renamed a widget.
  const [notice, setNotice] = useState<CanvasRestoreNotice | null>(null);
  const dismissNotice = useCallback(() => setNotice(null), []);

  useEffect(() => {
    if (!enabled || restored.current) return;
    restored.current = true;
    const { specs: fromUrl, unknown, malformed, dropped } = decodeCanvas(
      searchParams.get(CANVAS_PARAM),
      (name) => registry.has(name),
    );
    if (unknown.length || malformed) {
      // Still logged — a developer reading a bug report wants the names — but the log is no
      // longer the only place it goes.
      console.warn(
        `[canvas] this link could not be fully restored${
          unknown.length ? `: ${unknown.join(', ')}` : ''
        }${malformed ? ' (part of the link could not be read)' : ''}.`,
      );
      setNotice({
        unknown,
        malformed,
        missing: unknown.length + dropped,
        restoredAny: fromUrl.length > 0,
      });
    }
    if (fromUrl.length) addSpecs(fromUrl);
    // The baseline is taken AFTER the restore's own `addSpecs`, read straight from the store
    // rather than from the `specs` this render closed over. Setting it from the render's
    // value made the watcher below see the restore itself as "the advisor changed the
    // canvas" and clear the notice in the same tick it was set — visible only in a render
    // test, which is why AC1 is asserted on screen and not on the hook's return value.
    restoredTiles.current = tileFingerprint(useCanvasStore.getState().specs);
  }, [enabled, searchParams, addSpecs]);

  // TASK-842 AC4: the report is about the RESTORE, so it must not outlive the canvas that was
  // restored. Once the advisor has changed the canvas — opened a tile, closed one — the notice
  // is describing something that is no longer on screen, so it goes. `restored.current` gates
  // it so the restore's own `addSpecs` does not immediately clear the notice it just set.
  const tiles = tileFingerprint(specs);
  useEffect(() => {
    if (restoredTiles.current === null) return;              // restore has not run yet
    // Compared against the LIVE store, not the `specs` this render closed over. That render
    // still holds the PRE-restore value — the restore effect above runs in the same commit —
    // so comparing it would read the restore itself as "the advisor changed the canvas" and
    // clear the notice in the tick it was set.
    //
    // An earlier attempt guarded that with a latch that settled once a render's fingerprint
    // matched the snapshot exactly. Review found it could never close: anything else writing
    // to the store between the snapshot and the next commit — a sibling page's mount effect
    // calling `addSpecs`, an in-flight summon resolving — makes the first post-restore
    // fingerprint differ, and the latch then rejects every later change too, leaving the
    // notice on screen for ever describing a canvas the advisor has since rebuilt. Reading
    // the live value removes the ordering assumption rather than adding a guard for it.
    const live = tileFingerprint(useCanvasStore.getState().specs);
    if (live !== restoredTiles.current) {
      restoredTiles.current = live;
      setNotice(null);
    }
  }, [tiles]);

  useEffect(() => {
    if (!enabled || !restored.current) return;
    const encoded = encodeCanvas(specs);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (encoded === null) next.delete(CANVAS_PARAM);
        else next.set(CANVAS_PARAM, encoded);
        return next;
      },
      { replace: true },
    );
  }, [enabled, specs, setSearchParams]);

  return { restoreNotice: notice, dismissRestoreNotice: dismissNotice };
}
